import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { serverUpdateScreensharing } from "api/api-server-actions";
import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { CrossRemoveIcon, PresentPlayFilledIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectScreensharingClassUserId,
  selectUserByClassUserId,
  selectViewingScreenshare,
} from "store/selectors";
import { selectCurrentBreakoutGroupId } from "store/selectors/breakouts";
import { selectActiveClassSessionId } from "store/selectors/class-session";
import {
  selectCurrentClassUserId,
  selectCurrentClassUserIsPerformingAvCheck,
} from "store/selectors/class-user";
import { viewStateActions } from "store/slices/view";
import { useObjectSelector } from "store/store-hooks";
import { usePresentation, usePresentationBannerText } from "utils/presentation";
import { useNavigate, useQueryParamsActions } from "utils/routing";
import { pageUrl, useViewLevel, withQueryParams } from "utils/urls";
import { PROFILE_PROPERTIES } from "utils/user-utils";

type MenuState = {
  bannerText: string;
  buttonClassName?: string;
  actionIcon: ReactNode;
  actionText: string;
  eventName: string;
  onClick: () => void | Promise<void>;
};

// Green and orange square icon buttons are not part of the MDS, so these are one-offs.
const GREEN_BUTTON_CLASSES =
  "group-data-[dark]/themable:text-white group-data-[dark]/themable:bg-green-shade-40 group-data-[dark]/themable:border-green-shade-20 " +
  "group-data-[dark]/themable:hover:bg-green-shade-20 group-data-[dark]/themable:hover:text-white " +
  "group-data-[dark]/themable:active:bg-green-shade-20 group-data-[dark]/themable:active:border-green-shade-20 " +
  "group-data-[dark]/themable:data-[selected]:bg-green-shade-40 group-data-[dark]/themable:data-[selected]:border-green-shade-20 group-data-[dark]/themable:data-[selected]:hover:bg-green-shade-20 group-data-[dark]/themable:data-[selected]:hover:border-green-shade-20";

const ORANGE_BUTTON_CLASSES =
  "group-data-[dark]/themable:text-white group-data-[dark]/themable:bg-orange-shade-20 group-data-[dark]/themable:border-orange " +
  "group-data-[dark]/themable:hover:bg-orange group-data-[dark]/themable:hover:text-white " +
  "group-data-[dark]/themable:active:bg-orange-shade-20 group-data-[dark]/themable:active:border-orange " +
  "group-data-[dark]/themable:data-[selected]:bg-orange-shade-20 group-data-[dark]/themable:data-[selected]:border-orange group-data-[dark]/themable:data-[selected]:hover:bg-orange group-data-[dark]/themable:data-[selected]:hover:border-orange";

// For students only
export const MobilePresentationBanner = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { deleteQueryParams, setQueryParams } = useQueryParamsActions();

  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const activeClassSessionId = useAppSelector(selectActiveClassSessionId);
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const screensharingUser = useObjectSelector(
    (state) => selectUserByClassUserId(state, screensharingClassUserId),
    PROFILE_PROPERTIES,
  );
  const isViewingScreenshare = useAppSelector(selectViewingScreenshare);
  const isPerformingAvCheck = useAppSelector(selectCurrentClassUserIsPerformingAvCheck);

  const viewLevel = useViewLevel();
  const { bannerText: presentationBannerText } = usePresentationBannerText(viewLevel, false);
  const { isFollowing, hasStudentLeftPresentation, presentedPageId } = usePresentation();

  const screensharingUserName = screensharingUser?.first_name || "";
  const isMyScreenshare = !!(
    screensharingClassUserId && currentClassUserId === screensharingClassUserId
  );

  // Track whether the user has already followed the current presentation/screenshare session,
  // so we don't auto-follow them again if they explicitly stop following.
  const hasFollowedPresentationRef = useRef(false);
  const hasFollowedScreenshareRef = useRef(false);

  useEffect(() => {
    if (isFollowing) {
      hasFollowedPresentationRef.current = true;
    }
  }, [isFollowing]);

  useEffect(() => {
    if (!presentedPageId) {
      hasFollowedPresentationRef.current = false;
    }
  }, [presentedPageId]);

  useEffect(() => {
    if (isViewingScreenshare) {
      hasFollowedScreenshareRef.current = true;
    }
  }, [isViewingScreenshare]);

  useEffect(() => {
    if (!screensharingClassUserId) {
      hasFollowedScreenshareRef.current = false;
    }
  }, [screensharingClassUserId]);

  // Auto-follow a screenshare when one starts (takes priority over presentation).
  useEffect(() => {
    if (
      hasFollowedScreenshareRef.current ||
      !screensharingClassUserId ||
      isMyScreenshare ||
      isViewingScreenshare
    ) {
      return;
    }
    dispatch(viewStateActions.setViewingScreenshare(true));
    setQueryParams({ [FOLLOWING_QUERY_PARAM]: "active" });
    hasFollowedScreenshareRef.current = true;
  }, [dispatch, isMyScreenshare, isViewingScreenshare, screensharingClassUserId, setQueryParams]);

  // Auto-follow a presentation when one starts (unless a screenshare is already active).
  useEffect(() => {
    if (
      hasFollowedPresentationRef.current ||
      isFollowing ||
      isPerformingAvCheck ||
      screensharingClassUserId ||
      !presentedPageId ||
      !currentSectionId
    ) {
      return;
    }
    navigate(
      withQueryParams(pageUrl(currentSectionId, presentedPageId), {
        [FOLLOWING_QUERY_PARAM]: "active",
      }),
    );
    hasFollowedPresentationRef.current = true;
  }, [
    currentSectionId,
    isFollowing,
    isPerformingAvCheck,
    navigate,
    presentedPageId,
    screensharingClassUserId,
  ]);

  const menuState = useMemo((): MenuState => {
    // Screenshare states take priority over presentation states
    if (isMyScreenshare) {
      return {
        bannerText: t("av.screenshare.banner.screensharing"),
        buttonClassName: GREEN_BUTTON_CLASSES,
        actionIcon: <CrossRemoveIcon />,
        actionText: t("av.screenshare.end_screenshare"),
        eventName: "Screensharing - Stop",
        onClick: async () => {
          if (!activeClassSessionId) return;
          await serverUpdateScreensharing(activeClassSessionId, null, currentBreakoutGroupId);
        },
      };
    }

    if (isViewingScreenshare) {
      return {
        bannerText: t("av.screenshare.banner.following_screenshare", {
          user: screensharingUserName,
        }),
        actionIcon: <CrossRemoveIcon />,
        actionText: t("common.stop"),
        eventName: "Screensharing - Stop following",
        onClick: () => {
          dispatch(viewStateActions.setViewingScreenshare(false));
          deleteQueryParams([FOLLOWING_QUERY_PARAM]);
        },
      };
    }

    if (screensharingClassUserId) {
      return {
        bannerText: t("av.screenshare.banner.screenshare_in_progress", {
          user: screensharingUserName,
        }),
        buttonClassName: GREEN_BUTTON_CLASSES,
        actionIcon: <PresentPlayFilledIcon />,
        actionText: t("main_header.start_following"),
        eventName: "Screensharing - Start following",
        onClick: () => {
          dispatch(viewStateActions.setViewingScreenshare(true));
          setQueryParams({ [FOLLOWING_QUERY_PARAM]: "active" });
        },
      };
    }

    // Presentation states
    const navigateToPresentedPage = () => {
      if (!currentSectionId || !presentedPageId) return;
      navigate(
        withQueryParams(pageUrl(currentSectionId, presentedPageId), {
          [FOLLOWING_QUERY_PARAM]: "active",
        }),
      );
    };

    if (!isFollowing && !hasStudentLeftPresentation) {
      return {
        bannerText: presentationBannerText,
        buttonClassName: GREEN_BUTTON_CLASSES,
        actionIcon: <PresentPlayFilledIcon />,
        actionText: t("main_header.follow"),
        eventName: "Presentation - Follow along",
        onClick: navigateToPresentedPage,
      };
    }

    if (hasStudentLeftPresentation) {
      return {
        bannerText: presentationBannerText,
        buttonClassName: ORANGE_BUTTON_CLASSES,
        actionIcon: <PresentPlayFilledIcon />,
        actionText: t("common.return"),
        eventName: "Presentation - Return",
        onClick: navigateToPresentedPage,
      };
    }

    return {
      bannerText: presentationBannerText,
      actionIcon: <CrossRemoveIcon />,
      actionText: t("common.stop"),
      eventName: "Presentation - Stop following",
      onClick: () => {
        deleteQueryParams([FOLLOWING_QUERY_PARAM]);
      },
    };
  }, [
    activeClassSessionId,
    currentBreakoutGroupId,
    currentSectionId,
    deleteQueryParams,
    dispatch,
    isFollowing,
    hasStudentLeftPresentation,
    isMyScreenshare,
    isViewingScreenshare,
    navigate,
    presentationBannerText,
    presentedPageId,
    screensharingClassUserId,
    screensharingUserName,
    setQueryParams,
  ]);

  if (!screensharingClassUserId && !presentedPageId) return null;

  const MenuButton = (
    <SquareIconButton
      aria-label={t("presentation.presentation_banner_label")}
      className={twMerge(menuState.buttonClassName)}
      eventName="Mobile Presentation Banner menu opened"
      kind="tertiary"
      size="xl"
      isSelected
    >
      <PresentPlayFilledIcon />
    </SquareIconButton>
  );

  return (
    <Menu
      className="dark-menu"
      direction="top"
      menuButton={MenuButton}
      menuClassName="xs:min-w-[320px]"
    >
      <MenuItem disabled>{menuState.bannerText}</MenuItem>
      <MenuDivider />
      <MenuItem eventName={menuState.eventName} onClick={menuState.onClick}>
        <IconText iconStart={menuState.actionIcon} text={menuState.actionText} />
      </MenuItem>
    </Menu>
  );
};
